/**
 * FormModal — the app's canonical centered dialog shell for presenting
 * create/edit record forms as popups. Nothing generic existed before this:
 * every form-in-an-overlay was either a bespoke drawer or, in StateEditor's
 * case, a one-off centered panel that duplicated the drawer's plumbing by
 * hand. This component fuses the two proven precedents instead of inventing
 * a third pattern:
 *   - Plumbing (portal, scrim token, entry animation, Escape/click-away) is
 *     copied verbatim from CreateProjectDrawer.
 *   - Chrome (centered flex wrapper, panel sizing, header+close) is copied
 *     verbatim from StateEditor.
 * z-band contract: drawers occupy 199 (scrim) / 200 (panel); centered modals
 * occupy 299 (scrim) / 300 (panel) — one band above drawers so a centered
 * modal opened from within a drawer (or over one) always wins visually.
 * Parents mount this conditionally ({showForm && <FormModal ...>}) rather
 * than passing an `open` prop — there is no closed-but-mounted state.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

// Entry motion — same idiom/timing as CreateProjectDrawer's DRAWER_MOTION_CSS,
// renamed to avoid colliding with the drawer's keyframes/classnames since both
// can be mounted at once (a centered modal opened from within a drawer).
const FORM_MODAL_MOTION_CSS = `
@keyframes seedzModalIn { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: none; } }
@keyframes seedzModalScrimIn { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .seedz-modal-panel, .seedz-modal-scrim { animation: none !important; }
}
`;

interface FormModalProps {
  title: string;
  onClose: () => void;
  /** Panel width in px. Defaults to 440 (StateEditor's size). */
  width?: number;
  children: ReactNode;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function FormModal({ title, onClose, width = 440, children }: FormModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Escape key: close the modal.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Click-away dismiss — same outside-mousedown idiom as CreateProjectDrawer.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Focus management: capture the trigger, move focus into the panel on mount UNLESS a
  // descendant already holds focus (e.g. an input with autoFocus — React applies autoFocus
  // synchronously during commit, before this effect runs, so unconditionally calling
  // panelRef.current?.focus() here would clobber it — see FieldEditorModal's Label input,
  // A8 fix), and restore focus to the trigger on unmount.
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    if (!panelRef.current?.contains(document.activeElement)) {
      panelRef.current?.focus();
    }
    return () => {
      previouslyFocusedRef.current?.focus();
    };
  }, []);

  // Tab focus-trap: keep focus looping within the panel's focusable elements.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !panelRef.current.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !panelRef.current.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Portal to <body>: the routed page wrapper's .route-enter transform would
  // otherwise trap position:fixed and clip this modal to the page box (same
  // reason CreateProjectDrawer portals out).
  return createPortal(
    <>
      <style>{FORM_MODAL_MOTION_CSS}</style>
      {/* Scrim — real cockpit scrim token, not an ad-hoc color-mix. */}
      <div
        className="seedz-modal-scrim"
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--scrim-cockpit)',
          zIndex: 299,
          animation: 'seedzModalScrimIn 240ms var(--ease-out) both',
        }}
      />
      {/* Centered wrapper — StateEditor's flex-center chrome. z:300 is the
          shared centered-overlay band (also StateEditor + the CrLinkCell/
          RaidLinkCell popovers — audited: they never co-occur, so a shared
          number is safe; do not renumber piecemeal). */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          className="seedz-modal-panel"
          style={{
            width,
            maxWidth: 'calc(100vw - 48px)',
            maxHeight: 'calc(100vh - 64px)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-2)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            animation: 'seedzModalIn 240ms var(--ease-out) both',
            outline: 'none',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '14px 20px',
              borderBottom: '1px solid var(--border)',
              flexShrink: 0,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-base)',
                fontWeight: 700,
                color: 'var(--text)',
              }}
            >
              {title}
            </h2>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-lg)',
                color: 'var(--text-muted)',
                lineHeight: 1,
                padding: '2px 4px',
                borderRadius: 'var(--radius-xs)',
              }}
            >
              ×
            </button>
          </div>

          {/* Body — scrollable region for the form. */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>{children}</div>
        </div>
      </div>
    </>,
    document.body,
  );
}
